import {
  type HttpEvent,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { type Observable, switchMap, take } from 'rxjs';
import { AuthService } from '@/app/core/services/auth.service';
import { AuthStorageService } from '@/app/core/services/auth-storage.service';
import { HANDLE_401_LOCALLY } from './auth-request-context';

/**
 * Threshold in minutes before token expiration to trigger refresh
 * If token expires in less than this time, it will be refreshed proactively
 */
const TOKEN_REFRESH_THRESHOLD_MINUTES = 9.5;

/**
 * HTTP Interceptor that proactively refreshes token before it expires
 * Checks if token is expiring soon (within threshold) before each request
 * and refreshes it if needed to prevent 401 errors
 */
export const proactiveTokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authStorage = inject(AuthStorageService);
  const authService = inject(AuthService);
  const handles401Locally = req.context.get(HANDLE_401_LOCALLY);

  // Skip token refresh check for authentication endpoints to avoid infinite loops
  const isAuthEndpoint =
    req.url.includes('/login') ||
    req.url.includes('/refresh') ||
    req.url.includes('/oauth') ||
    req.url.includes('/whoami') ||
    req.url.includes('/adminusers/activation');

  // Skip if this is an auth endpoint or no token exists
  if (isAuthEndpoint || handles401Locally || !authStorage.hasToken()) {
    return next(req);
  }

  // Check if token is already expired
  if (authStorage.isTokenExpired()) {
    // Token already expired, let the reactive interceptor handle it (401 response)
    return next(req);
  }

  // Check if token is expiring soon (within threshold)
  if (authStorage.isTokenExpiringSoon(TOKEN_REFRESH_THRESHOLD_MINUTES)) {
    // Token is expiring soon, refresh it proactively before making the request
    return refreshAndRetry(authService, authStorage, req, next);
  }

  // Token is still valid and not expiring soon, proceed with the request
  return next(req);
};

/**
 * Refresh token and retry the original request with new token
 */
function refreshAndRetry(
  authService: AuthService,
  authStorage: AuthStorageService,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  // Call refresh endpoint
  return authService.refreshToken().pipe(
    take(1),
    switchMap(() => {
      // Get the fresh token from storage (it was saved by AuthService.refreshToken)
      const token = authStorage.getToken();

      if (token == null || token === '') {
        // This shouldn't happen, but handle gracefully
        return next(req);
      }

      // Clone the original request with new token
      const clonedRequest = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Retry the request with refreshed token
      return next(clonedRequest);
    }),
  );
}
